import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import '../../styles/websitepagescss/About.css';

// Import team member images
import EHSANURREHMAN from '../../assets/images/team/6.png';
import MARIANAFEES from '../../assets/images/team/2.png';
import maqboolAhmedkhan from '../../assets/images/team/1.png';
import TAHIRABBAS from '../../assets/images/team/5.png';
import MuftiRaeesAhmed  from '../../assets/images/team/4.jpg';
import saleemahmed   from '../../assets/images/team/3.png';

const About = () => {
  const [selectedMember, setSelectedMember] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageLoading, setImageLoading] = useState({});

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12
      }
    }
  };

  const handleImageLoad = (memberName) => {
    setImageLoading(prev => ({ ...prev, [memberName]: true }));
  };

  const teamMembers = [
  { 
    name: "Engr. Maqbool Ahmed Khan",
    role: "STRUCTURE ENGINEER", 
    image: maqboolAhmedkhan,
    shortDescription: "Designing and analyzing structural systems for optimal safety and long-term building durability and reliability",
    fullDescription: "Engr. Maqbool Ahmed Khan is our structural engineering expert with extensive experience in analyzing and designing robust building structures. He specializes in calculating loads, stresses, and strains to ensure our buildings can withstand environmental forces and usage demands. His technical expertise in structural analysis and foundation design guarantees the safety and durability of all our constructions.",
    specialties: ["Structural Analysis", "Foundation"],
  },
  { 
    name: "Ar. Maria Nafees",
    role: "HEAD ARCHITECT", 
    image: MARIANAFEES,
    shortDescription: "Leading architectural design innovation and creative vision development for exceptional building aesthetics and functionality delivering  solutions.",
    fullDescription: "Ar. Maria Nafees is an accomplished architect with a passion for innovative design and sustainable architecture. As Head of Architecture, she leads our design team in creating functional, aesthetically pleasing spaces that meet client needs while incorporating modern architectural principles. Her designs seamlessly blend form and function, creating landmarks that stand the test of time.",
    specialties: ["Concept Design", "Sustainability"],
  },
  { 
    name: "Mufti Raees Ahmed", 
    role: "Director Marketing ", 
    image: MuftiRaeesAhmed,
    shortDescription: "Driving business growth through strategic marketing initiatives and effective public engagement strategies , partnerships and long-lasting stakeholder",
    fullDescription: "Mufti Raees Ahmed leads our marketing and public relations efforts with strategic vision and exceptional communication skills. He develops comprehensive marketing strategies that enhance our brand visibility and manages stakeholder relationships. His expertise in digital marketing and public relations has been crucial in expanding our market presence and maintaining our reputation as a trusted construction partner.",
    specialties: ["Brand Management", "Marketing"],
  },
  { 
    name: "Engr. Saleem Ahmed",
    role: "ARCHITECT", 
    image: saleemahmed,
    shortDescription: "Creating innovative architectural designs and technical solutions that balance creativity with practical implementation and ensuring structural efficiency and sustainability.",
    fullDescription: "Engr. Saleem Ahmed is a skilled architect with expertise in transforming client visions into practical, beautiful designs. He specializes in developing detailed architectural plans, coordinating with engineering teams, and ensuring design integrity throughout the construction process. His attention to detail and creative problem-solving skills contribute significantly to the aesthetic and functional excellence of our projects.",
    specialties: ["Technical Drawing", "Design"],
  },
  { 
    name: "Engr. Ehsan Ur Rehman",
    role: "RESIDENT ENGINEER", 
    image: EHSANURREHMAN,
    shortDescription: "Overseeing on-site construction operations and maintaining strict quality control standards and safety protocols and ensuring timely project delivery and reliable performance across all phases.",
    fullDescription: "Engr. Ehsan ur Rehman brings over two decades of hands-on experience in construction supervision and project execution. As Resident Engineer, he ensures all construction activities comply with design specifications, building codes, and safety standards. His meticulous site management and technical expertise have been instrumental in delivering numerous successful projects on time and within budget.",
    specialties: ["Site Supervision", "Quality"],
  },
  { 
    name: "Tahir Abbas",
    role: "PROCUREMENT MANAGER", 
    image: TAHIRABBAS,
    shortDescription: "Managing material sourcing strategies and optimizing supply chain operations for cost efficiency, reliability, and timely delivery to support smooth construction workflow and project success.",
    fullDescription: "Tahir Abbas excels in procurement and supply chain management, ensuring timely availability of quality materials at competitive prices. As Procurement Manager, he develops strategic vendor relationships, negotiates contracts, and maintains optimal inventory levels. His efficient procurement strategies have significantly reduced project costs while maintaining the highest quality standards.",
    specialties: ["Vendor Management", "Contracts"],
  },
];

  const handleMemberClick = (member) => {
    setSelectedMember(member);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = 'unset';
    setTimeout(() => setSelectedMember(null), 300);
  };

  return (
    <motion.div 
      className="about-page"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Background Pattern */}
      <div className="about-background-pattern"></div>
      
      {/* Full Width Background Color for Hero Section */}
      <div className="hero-full-background">
        <div className="container">
          {/* Hero Section with Background */}
          <motion.div 
            className="about-hero"
            variants={containerVariants}
          >
            {/* Background Image Container - Only 2 tags used */}
            <div className="hero-background-container">
              <div className="hero-background-overlay"></div>
            </div>
            
            <motion.h1 variants={itemVariants}>About Our Company</motion.h1>
            <motion.div 
              className="about-intro"
              variants={itemVariants}
            >
              <strong>Welcome</strong> to Meezan Developers, where our name derived from 
              the Arabic for 'BALANCE' & 'SCALES' epitomizes our foundational principle: 
              the meticulous equilibrium of aesthetic vision, structural integrity, and 
              client value. Since our establishment in 2009, we have distinguished ourselves 
              as a premier, fully integrated firm, seamlessly uniting architectural innovation 
              with master-level construction execution to deliver exceptional projects across 
              the entire built environment.
            </motion.div>
          </motion.div>
        </div>
      </div>

      <div className="container">
        {/* About Sections */}
        <motion.div 
          className="about-sections"
          variants={containerVariants}
        >
          <motion.section 
            className="about-section"
            variants={itemVariants}
            whileHover={{ y: -8, scale: 1.02 }}
          >
            <div className="section-icon">📜</div>
            <h2>Our History</h2>
            <p>
              Since our inception in 2009, Meezan Developers has built a legacy defined by a 
              relentless pursuit of construction excellence. Our journey began as a specialized 
              contractor and has evolved significantly, establishing us as a multifaceted industry leader.
              <br /><br />
              Our early years were marked by a focus on residential construction, where we earned 
              our reputation for building high-quality, durable homes and apartment complexes. This 
              foundational success provided the springboard for expansion into commercial development.
              <br /><br />
              Further demonstrating our versatility, we have made significant contributions to 
              the religious, industrial and institutional sectors, showcasing our ability to adapt 
              our expertise to the unique technical and regulatory demands of diverse industries.
            </p>
          </motion.section>
          
          <motion.section 
            className="about-section"
            variants={itemVariants}
            whileHover={{ y: -8, scale: 1.02 }}
          >
            <div className="section-icon">🎯</div>
            <h2>Our Mission</h2>
            <p>
              To pioneer the future of urban living in Pakistan by developing superior, sustainable, 
              and intelligent properties. We are committed to:
              <br /><br />
               <strong>Quality Durability:</strong> Employing exceptional materials and engineering 
              excellence to ensure lasting value.
              <br /><br />
               <strong>Client Centricity:</strong> Fostering transparent, long-term partnerships by 
              delivering on our commitments and exceeding expectations.
              <br /><br />
               <strong>Ethical Integrity:</strong> Conducting all business with unwavering honesty and 
              transparency, building a foundation of trust.
              <br /><br />
               <strong>Innovative Solutions:</strong> Integrating cutting-edge technology and forward-thinking 
              design to enhance lifestyle and operational efficiency.
            </p>
          </motion.section>
          
          <motion.section 
            className="about-section"
            variants={itemVariants}
            whileHover={{ y: -8, scale: 1.02 }}
          >
            <div className="section-icon">🔭</div>
            <h2>Our Vision</h2>
            <p>
              To be the most trusted and transformative real estate developer in Pakistan, setting the 
              benchmark for quality and innovation. We envision shaping sustainable urban landscapes 
              that not only meet the demands of the present but also anticipate the needs of future 
              generations, thereby contributing significantly to the nation's progress.
              <br /><br />
              This strategic growth, fueled by landmark projects and enduring client partnerships, has 
              solidified our position as a cornerstone of Pakistan's construction sector. We have 
              consistently navigated market evolutions and technological advancements, emerging not 
              merely as participants, but as leaders who set benchmarks for quality, reliability, and 
              innovation across every field we engage in.
              <br /><br />
              <strong>Meezan Developers – Building Value, Delivering Trust.</strong>
            </p>
          </motion.section>
        </motion.div>

        {/* Team Section */}
        <motion.div 
          className="team-section"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-50px" }}
        >
          <div className="section-header">
            <h2>Meet Our Core Team</h2>
            <p className="team-description-text">Our experienced leadership team brings decades of combined expertise in construction, architecture, and project management.</p>
          </div>
          <div className="team-grid">
            {teamMembers.map((member, index) => (
              <motion.div 
                key={member.name}
                className="team-card"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                whileHover={{ 
                  y: -8,
                  transition: { type: "spring", stiffness: 300 }
                }}
                transition={{ 
                  delay: index * 0.1,
                  type: "spring", 
                  stiffness: 100 
                }}
                viewport={{ once: true, margin: "-50px" }}
                onClick={() => handleMemberClick(member)}
              >
                <div className="team-image-container">
                  {!imageLoading[member.name] && (
                    <div className="image-skeleton">
                      <div className="skeleton-loader"></div>
                    </div>
                  )}
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className={`team-image ${imageLoading[member.name] ? 'loaded' : 'loading'}`}
                    loading="lazy"
                    onLoad={() => handleImageLoad(member.name)}
                  />
                  <div className="image-overlay">
                    <span>View Profile</span>
                  </div>
                </div>
                <div className="team-content">
                  <h3>{member.name}</h3>
                  <p className="team-role">{member.role}</p>
                  <p className="team-description">{member.shortDescription}</p>
                  <div className="team-specialties">
                    {member.specialties.map((specialty, idx) => (
                      <span key={idx} className="specialty-tag">
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Team Member Modal */}
      <AnimatePresence>
        {isModalOpen && selectedMember && (
          <motion.div
            className="team-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div
              className="team-modal"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className="close-button" onClick={closeModal}>
                &times;
              </button>
              
              <div className="modal-header">
                <div className="modal-image-section">
                  <div className="modal-image-container">
                    <img 
                      src={selectedMember.image} 
                      alt={selectedMember.name}
                      className="modal-profile-image"
                    />
                  </div>
                </div>
                <div className="modal-title-section">
                  <h2>{selectedMember.name}</h2>
                  <p className="modal-role">{selectedMember.role}</p>
                </div>
              </div>
              
              <div className="modal-content">
                <div className="modal-section">
                  <h4>About</h4>
                  <p className="modal-description">{selectedMember.fullDescription}</p>
                </div>

                <div className="modal-section">
                  <h4>Areas of Expertise</h4>
                  <div className="expertise-tags">
                    {selectedMember.specialties.map((specialty, index) => (
                      <span key={index} className="expertise-tag">
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default About;